import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, triggerGlobalUserRefresh } from '../context/AuthContext';
import {
  Mic, MicOff, Square, ArrowLeft, Loader2, Volume2, VolumeX,
  Send, Code, Users, Briefcase, BookOpen, Sparkles, Video, VideoOff,
  CameraOff, MessageSquare, ChevronDown, ChevronUp, Download, Share2, Copy, X, Check, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';

const SECTION_META = {
  skills: { label: 'Skills', icon: <Code className="w-3 h-3" />, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  projects: { label: 'Projects', icon: <Briefcase className="w-3 h-3" />, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  experience: { label: 'Experience', icon: <Users className="w-3 h-3" />, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  fundamentals: { label: 'Fundamentals', icon: <BookOpen className="w-3 h-3" />, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  introduction: { label: 'Introduction', icon: <Sparkles className="w-3 h-3" />, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
};

const SECTION_GLOW = {
  introduction: 'from-red-600/20 via-red-900/10 to-transparent',
  projects: 'from-emerald-500/20 via-emerald-950/10 to-transparent',
  experience: 'from-amber-500/20 via-amber-950/10 to-transparent',
  skills: 'from-blue-500/20 via-blue-950/10 to-transparent',
  fundamentals: 'from-purple-500/20 via-purple-950/10 to-transparent',
};

function deduplicateText(text) {
  if (!text) return '';
  let str = text.trim();

  const words = str.split(/\s+/);
  const cleanWords = [];
  for (let i = 0; i < words.length; i += 1) {
    if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
      cleanWords.push(words[i]);
    }
  }
  str = cleanWords.join(' ');

  for (let len = 8; len >= 2; len -= 1) {
    const pattern = new RegExp(`(\\b(?:\\w+\\s+){${len - 1}}\\w+\\b)\\s+\\1\\b`, 'gi');
    str = str.replace(pattern, '$1');
  }

  return str;
}

// TTS Helper
const speakText = (text, onStart, onEnd, voiceRef) => {
  return new Promise((resolve) => {
    if (!window.speechSynthesis) { resolve(); return; }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 0.95;
    utterance.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
      v.name.includes('Google UK English Male') || v.name.includes('Daniel') || v.name.includes('James') ||
      (v.lang.startsWith('en') && v.name.toLowerCase().includes('male'))
    ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
    if (preferred) utterance.voice = preferred;
    utterance.onstart = () => onStart?.();
    utterance.onend = () => { onEnd?.(); resolve(); };
    utterance.onerror = () => { onEnd?.(); resolve(); };
    if (voiceRef) voiceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  });
};

export default function InterviewPage() {
  const { interviewId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentSpokenText, setCurrentSpokenText] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [responseError, setResponseError] = useState('');
  const [interviewTime, setInterviewTime] = useState(0);
  const [interviewState, setInterviewState] = useState(location.state?.initialState || null);
  const [interviewConfig, setInterviewConfig] = useState(location.state?.config || null);

  // Video & Webcam State
  const [cameraOn, setCameraOn] = useState(true);
  const [cameraCollapsed, setCameraCollapsed] = useState(false);
  const videoRef = useRef(null);

  // Screen Recording & Social Sharing Modal State
  const [screenRecordingActive, setScreenRecordingActive] = useState(false);
  const [screenRecordingUrl, setScreenRecordingUrl] = useState(null);
  const [showScreenPrompt, setShowScreenPrompt] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [completedReportId, setCompletedReportId] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const mediaRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const screenChunksRef = useRef([]);
  const screenStreamRef = useRef(null);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const isUserRecordingRef = useRef(false);
  const finalSavedTranscriptRef = useRef('');
  const currentSessionTranscriptRef = useRef('');
  const utteranceRef = useRef(null);
  const timerRef = useRef(null);
  const chatEndRef = useRef(null);

  // Prompt for Screen Recording on Mount if supported
  useEffect(() => {
    if (navigator.mediaDevices?.getDisplayMedia && !screenRecordingActive && !showScreenPrompt && !showShareModal) {
      setShowScreenPrompt(true);
    }
  }, []); // eslint-disable-line

  const requestScreenRecording = async () => {
    try {
      setShowScreenPrompt(false);
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" },
        audio: true,
      });
      screenStreamRef.current = stream;
      screenChunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      screenRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          screenChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(screenChunksRef.current, { type: 'video/webm' });
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setScreenRecordingUrl(url);
        }
      };

      recorder.start(1000);
      setScreenRecordingActive(true);
      toast.success("Screen recording active for your interview session.");
    } catch (err) {
      console.warn("Screen recording permission denied or unsupported:", err);
      toast.info("Proceeding without screen recording.");
      setShowScreenPrompt(false);
    }
  };

  const stopScreenRecording = () => {
    if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
      try {
        screenRecorderRef.current.stop();
      } catch (e) {}
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    setScreenRecordingActive(false);
  };

  // Auto-scroll chat thread
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, liveTranscript]);

  // Webcam Stream Handler
  useEffect(() => {
    let stream = null;
    if (cameraOn && navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: { width: 320, height: 240 }, audio: false })
        .then((s) => {
          stream = s;
          if (videoRef.current) {
            videoRef.current.srcObject = s;
          }
        })
        .catch((err) => {
          console.warn('Webcam stream error:', err);
        });
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraOn]);

  useEffect(() => {
    const loadVoices = () => window.speechSynthesis?.getVoices();
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
  }, []);

  useEffect(() => {
    const startedAt = interviewState?.started_at;
    if (!startedAt) return undefined;

    const syncElapsedTime = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
      setInterviewTime(elapsed);
    };

    syncElapsedTime();
    timerRef.current = setInterval(syncElapsedTime, 1000);
    return () => clearInterval(timerRef.current);
  }, [interviewState?.started_at]);

  useEffect(() => {
    fetchInterview();
    const mediaRecorder = mediaRecorderRef.current;
    return () => {
      window.speechSynthesis?.cancel();
      isUserRecordingRef.current = false;
      recognitionRef.current?.abort?.();
      stopScreenRecording();
      if (mediaRecorder?.stream) {
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [interviewId]); // eslint-disable-line

  const fetchInterview = async () => {
    try {
      const res = await api.get(`/interviews/${interviewId}`);
      const data = res.data;
      setMessages(data.messages || []);
      if (data.state) setInterviewState(data.state);
      if (data.config) setInterviewConfig(data.config);
      const lastAi = [...(data.messages || [])].reverse().find(m => m.role === 'assistant');
      if (lastAi && !muted) {
        setCurrentSpokenText(lastAi.content);
        speakText(lastAi.content, () => setIsSpeaking(true), () => setIsSpeaking(false), utteranceRef);
      }
    } catch (err) {
      console.error('Failed to fetch interview:', err);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const durationSec = (interviewConfig?.duration || 15) * 60;
  const remainingTime = Math.max(0, durationSec - interviewTime);
  const timeProgress = Math.min(100, (interviewTime / durationSec) * 100);
  const currentQ = interviewState?.current_question || 0;
  const totalQ = interviewState?.total_questions || 8;
  const questionProgress = Math.min(100, (currentQ / totalQ) * 100);
  const currentSection = interviewState?.current_section || 'introduction';
  const timeWarning = remainingTime < 120 && remainingTime > 0;

  useEffect(() => {
    if (!interviewState?.started_at || ending || sending) return;
    if (remainingTime === 0) {
      handleEndInterview({ skipConfirm: true });
    }
  }, [ending, interviewState?.started_at, remainingTime, sending]); // eslint-disable-line

  const handleSend = useCallback(async (textOverride) => {
    const userAnswer = (textOverride || input).trim();
    if (!userAnswer || sending) return;
    setVoiceError('');
    setResponseError('');
    setLiveTranscript('');
    setInput('');
    setSending(true);
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);

    setMessages(prev => [...prev, { role: 'user', content: userAnswer, timestamp: new Date().toISOString() }]);

    try {
      const res = await api.post('/next-question', {
        interview_id: interviewId, user_answer: userAnswer
      });

      const aiMsg = res.data.message;
      setMessages(prev => [...prev, { role: 'assistant', content: aiMsg, timestamp: new Date().toISOString() }]);
      if (res.data.state) setInterviewState(res.data.state);
      setCurrentSpokenText(aiMsg);

      if (!muted) {
        await speakText(aiMsg, () => setIsSpeaking(true), () => setIsSpeaking(false), utteranceRef);
      }

      if (res.data.auto_end) {
        await handleEndInterview({ skipConfirm: true });
      }
    } catch (err) {
      console.error('Error:', err);
      setResponseError(err?.response?.data?.detail || 'Kevin could not process that answer. Please try again.');
    } finally {
      setSending(false);
    }
  }, [input, sending, interviewId, muted]); // eslint-disable-line

  const handleEndInterview = async ({ skipConfirm = false } = {}) => {
    if (ending) return;
    if (!skipConfirm && !window.confirm('End interview? Kevin will generate your evaluation report.')) return;
    setEnding(true);
    isUserRecordingRef.current = false;
    window.speechSynthesis?.cancel();
    clearInterval(timerRef.current);
    stopScreenRecording();

    try {
      const res = await api.post('/end-interview', { interview_id: interviewId });
      triggerGlobalUserRefresh();
      setCompletedReportId(res.data.id);
      setShowShareModal(true);
    } catch (err) {
      console.error('End interview error:', err);
      const rptId = err.response?.data?.id || `rpt_${interviewId.replace('int_', '')}`;
      triggerGlobalUserRefresh();
      setCompletedReportId(rptId);
      setShowShareModal(true);
    } finally {
      setEnding(false);
    }
  };

  const handleProceedToReport = () => {
    setShowShareModal(false);
    if (completedReportId) {
      navigate(`/reports/${completedReportId}`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleCopyShareLink = () => {
    const link = "https://www.kevinhr.in";
    navigator.clipboard.writeText(`I just completed an AI Mock Interview with Kevin AI at ${link}! Check it out to practice your interviews: ${link}`);
    setLinkCopied(true);
    toast.success("Share text copied to clipboard! Paste on Instagram or socials.");
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const toggleMute = () => {
    if (!muted) { window.speechSynthesis?.cancel(); setIsSpeaking(false); }
    setMuted(!muted);
  };

  const submitVoiceTranscript = useCallback((transcript) => {
    const cleanTranscript = transcript.trim();
    setLiveTranscript('');
    if (!cleanTranscript) {
      setVoiceError('No clear speech detected. Please speak clearly or type your answer.');
      return;
    }
    setInput(cleanTranscript);
    handleSend(cleanTranscript);
  }, [handleSend]);

  const startRecording = async () => {
    setVoiceError('');
    setResponseError('');
    setLiveTranscript('');
    isUserRecordingRef.current = true;
    finalSavedTranscriptRef.current = '';
    currentSessionTranscriptRef.current = '';
    transcriptRef.current = '';

    try {
      window.speechSynthesis?.cancel(); setIsSpeaking(false);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        recognition.lang = 'en-US';
        recognition.interimResults = true;
        recognition.continuous = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => setIsRecording(true);

        recognition.onresult = (event) => {
          let finalChunks = [];
          let latestInterim = '';

          for (let i = 0; i < event.results.length; i += 1) {
            const res = event.results[i];
            const text = (res[0]?.transcript || '').trim();
            if (!text) continue;

            if (res.isFinal) {
              if (finalChunks.length > 0) {
                const lastIdx = finalChunks.length - 1;
                const prevText = finalChunks[lastIdx];
                if (text.toLowerCase().startsWith(prevText.toLowerCase())) {
                  finalChunks[lastIdx] = text;
                } else if (!prevText.toLowerCase().includes(text.toLowerCase())) {
                  finalChunks.push(text);
                }
              } else {
                finalChunks.push(text);
              }
            } else {
              latestInterim = text;
            }
          }

          let fullFinal = finalChunks.join(' ').trim();
          let combinedSession = '';

          if (latestInterim) {
            if (fullFinal && latestInterim.toLowerCase().startsWith(fullFinal.toLowerCase())) {
              combinedSession = latestInterim;
            } else if (fullFinal && fullFinal.toLowerCase().startsWith(latestInterim.toLowerCase())) {
              combinedSession = fullFinal;
            } else {
              combinedSession = `${fullFinal} ${latestInterim}`.trim();
            }
          } else {
            combinedSession = fullFinal;
          }

          currentSessionTranscriptRef.current = combinedSession;

          const history = finalSavedTranscriptRef.current.trim();
          let fullText = history;

          if (combinedSession) {
            if (history && combinedSession.toLowerCase().startsWith(history.toLowerCase())) {
              fullText = combinedSession;
            } else if (!history.toLowerCase().endsWith(combinedSession.toLowerCase())) {
              fullText = `${history} ${combinedSession}`.trim();
            }
          }

          const cleanPreview = deduplicateText(fullText);
          transcriptRef.current = cleanPreview;
          setLiveTranscript(cleanPreview);
          setInput(cleanPreview);
        };

        recognition.onerror = (event) => {
          if (event.error !== 'aborted' && event.error !== 'no-speech') {
            setVoiceError('Speech recognition issue. You can try speaking again or type your answer.');
          }
        };

        recognition.onend = () => {
          if (currentSessionTranscriptRef.current) {
            const prevHistory = finalSavedTranscriptRef.current.trim();
            const sessionText = currentSessionTranscriptRef.current.trim();
            if (prevHistory && sessionText.toLowerCase().startsWith(prevHistory.toLowerCase())) {
              finalSavedTranscriptRef.current = sessionText;
            } else if (!prevHistory.toLowerCase().endsWith(sessionText.toLowerCase())) {
              finalSavedTranscriptRef.current = `${prevHistory} ${sessionText}`.trim();
            }
            currentSessionTranscriptRef.current = '';
          }

          if (isUserRecordingRef.current) {
            try {
              recognition.start();
            } catch (err) {
              // Ignore if already starting/running
            }
          } else {
            setIsRecording(false);
            const finalText = transcriptRef.current;
            recognitionRef.current = null;
            if (finalText?.trim()) {
              submitVoiceTranscript(finalText);
            }
          }
        };

        recognition.start();
        return;
      }

      setVoiceError('Voice speech input requires Chrome or Edge. Please type your answer below.');
    } catch (err) {
      console.error('Recording failed:', err);
      setVoiceError('Microphone permission is required.');
    }
  };

  const stopRecording = () => {
    isUserRecordingRef.current = false;
    setIsRecording(false);
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const sectionMeta = SECTION_META[currentSection] || SECTION_META.introduction;
  const glowGradient = SECTION_GLOW[currentSection] || SECTION_GLOW.introduction;

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-[#050505] text-white overflow-y-auto lg:overflow-hidden font-sans select-none" data-testid="interview-page">
      
      {/* LEFT PANEL: 70% WIDTH ON DESKTOP, FULL WIDTH ON MOBILE */}
      <div className="w-full lg:w-[70%] flex flex-col min-h-[500px] lg:h-full border-b lg:border-b-0 lg:border-r border-white/10 relative overflow-hidden bg-[#050505] shrink-0">
        
        {/* Top Header Bar */}
        <header className="shrink-0 z-20 bg-[#08080B]/95 backdrop-blur-2xl border-b border-white/10 shadow-xl">
          <div className="px-6 py-3 flex flex-wrap items-center justify-between gap-3">
            
            {/* Left Status Indicators */}
            <div className="flex items-center gap-3">
              <button
                data-testid="back-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all border border-white/5"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_#EF4444]" />
                <span className="font-semibold text-gray-300">Live Interview Session</span>
              </div>
            </div>

            {/* Middle Section & Time */}
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${sectionMeta.bg} ${sectionMeta.color}`}>
                {sectionMeta.icon}
                <span>{sectionMeta.label}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-mono">
                <span className="text-gray-400">Time:</span>
                <span className={`font-bold ${timeWarning ? 'text-red-400 animate-pulse' : 'text-white'}`}>{formatTime(remainingTime)}</span>
              </div>
            </div>

            {/* Right Mute & End Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleMute}
                className={`p-2 rounded-xl border transition-all ${muted ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'}`}
                title={muted ? "Unmute AI Voice" : "Mute AI Voice"}
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button
                data-testid="end-interview-btn"
                onClick={() => handleEndInterview()}
                disabled={ending}
                className="netflix-btn-red px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md"
              >
                <Square className="w-3.5 h-3.5 fill-white" /> End Session
              </button>
            </div>
          </div>

          {/* Dual Progress Bars */}
          <div className="flex w-full h-1 bg-white/5">
            <div className="h-full bg-[#E50914] shadow-[0_0_10px_#E50914] transition-all duration-500" style={{ width: `${questionProgress}%` }} />
            <div className={`h-full transition-all duration-500 ${timeWarning ? 'bg-red-500 animate-pulse' : 'bg-blue-500/40'}`} style={{ width: `${timeProgress}%` }} />
          </div>
        </header>

        {/* Main Stage (Kevin AI Avatar Room) */}
        <main className="flex-1 min-h-0 relative flex flex-col items-center justify-center p-6 overflow-hidden">
          
          {/* Background Ambient Glow */}
          <div className={`pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,var(--tw-gradient-stops))] ${glowGradient} opacity-50 transition-all duration-1000`} />

          {/* Avatar & Voice Waves */}
          <div className="relative z-10 flex flex-col items-center justify-center my-auto space-y-6">
            <div className="relative flex items-center justify-center">
              <div className={`absolute inset-[-50px] rounded-full blur-3xl transition-all duration-700 ${isSpeaking ? 'bg-red-600/35 scale-125 opacity-100' : 'bg-blue-600/15 scale-100 opacity-50'}`} />
              <div className={`absolute inset-[-20px] rounded-full transition-all duration-500 border ${isSpeaking ? 'border-red-500/40 animate-ping-slow scale-110' : 'border-white/10 scale-100'}`} />
              
              <div className={`w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-gradient-to-b from-gray-900 to-black border-2 flex items-center justify-center relative z-10 transition-all duration-500 shadow-2xl ${
                isSpeaking ? 'border-[#E50914] shadow-[0_0_60px_rgba(229,9,20,0.5)] scale-105' : 'border-white/20 shadow-[0_0_30px_rgba(0,0,0,0.8)]'
              }`}>
                <span className="text-white font-black text-5xl sm:text-6xl tracking-tighter" style={{ fontFamily: 'Outfit' }}>K</span>
              </div>
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Kevin AI</h2>
              <p className="text-sm text-gray-400 mt-1 font-semibold">
                {sending ? 'Thinking...' : isSpeaking ? 'Speaking...' : isRecording ? 'Listening to your answer...' : transcribing ? 'Transcribing...' : 'Waiting for your answer'}
              </p>
            </div>

            {/* Dynamic Voice Waveform */}
            {(isSpeaking || isRecording) && (
              <div className="flex items-center justify-center gap-1.5 py-2" data-testid="voice-waveform">
                {Array.from({ length: 28 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-all ${isRecording ? 'bg-red-500 shadow-[0_0_10px_#EF4444]' : 'bg-blue-400 shadow-[0_0_10px_#60A5FA]'}`}
                    style={{
                      height: `${Math.sin(i + Date.now()) * 16 + 18}px`,
                      animation: `wave 0.7s ease-in-out ${i * 0.03}s infinite alternate`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* PIP Bottom Right Corner Webcam Box (Collapsible) */}
          {cameraCollapsed ? (
            <button
              onClick={() => setCameraCollapsed(false)}
              className="absolute bottom-6 right-6 z-30 px-3.5 py-2 rounded-full bg-[#0A0A0E]/90 backdrop-blur-xl border border-white/20 text-xs font-bold text-white flex items-center gap-2 shadow-2xl hover:bg-white/10 transition-all group"
              title="Expand Camera Preview"
            >
              <Video className="w-4 h-4 text-[#E50914] group-hover:scale-110 transition-transform" />
              <span>Camera</span>
              <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
            </button>
          ) : (
            <div className="absolute bottom-6 right-6 z-30 w-44 sm:w-56 h-32 sm:h-40 rounded-2xl border-2 border-white/20 shadow-[0_12px_40px_rgba(0,0,0,0.8)] bg-[#0A0A0E] overflow-hidden group">
              {cameraOn ? (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover transform -scale-x-100" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-[#0F0F12] text-gray-500">
                  <CameraOff className="w-8 h-8 mb-1 text-gray-600" />
                  <span className="text-[10px] uppercase font-bold tracking-wider">Camera Off</span>
                </div>
              )}

              {/* User Name Badge on Video Box */}
              <div className="absolute bottom-2 left-2 px-2.5 py-0.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-semibold text-white truncate max-w-[120px]">
                {user?.name || 'You'} (Candidate)
              </div>

              {/* Camera Collapse Button */}
              <button
                onClick={() => setCameraCollapsed(true)}
                className="absolute top-2 left-2 p-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-gray-300 hover:text-white hover:bg-black/90 transition-all opacity-80 group-hover:opacity-100"
                title="Collapse Camera Preview"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {/* Camera On/Off Toggle Button */}
              <button
                onClick={() => setCameraOn(!cameraOn)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-gray-300 hover:text-white hover:bg-black/90 transition-all opacity-80 group-hover:opacity-100"
                title={cameraOn ? "Turn Camera Off" : "Turn Camera On"}
              >
                {cameraOn ? <Video className="w-3.5 h-3.5" /> : <VideoOff className="w-3.5 h-3.5 text-red-400" />}
              </button>
            </div>
          )}
        </main>
      </div>

      {/* RIGHT PANEL: 30% WIDTH (Live Chat Thread & Controls) */}
      <div className="lg:w-[30%] flex flex-col h-full bg-[#09090C] border-l border-white/10 z-20">
        
        {/* Right Header */}
        <div className="p-4 border-b border-white/10 bg-[#0D0D12] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#E50914]" />
            <h3 className="text-sm font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Interview Transcript & Chat
            </h3>
          </div>
          <span className="text-[11px] text-gray-400 font-mono bg-white/5 border border-white/10 px-2.5 py-0.5 rounded-full">
            {messages.length} msgs
          </span>
        </div>

        {/* Chat Thread Messages Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-xl bg-gradient-to-tr from-[#B20710] to-[#E50914] text-white flex items-center justify-center shrink-0 text-xs font-bold shadow-md" style={{ fontFamily: 'Outfit' }}>
                  K
                </div>
              )}

              <div className={`max-w-[88%] space-y-1 ${msg.role === 'user' ? 'items-end text-right' : 'items-start'}`}>
                <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider block">
                  {msg.role === 'assistant' ? 'Kevin AI' : user?.name || 'You'}
                </span>
                <div className={`p-3.5 rounded-2xl text-xs leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#E50914] text-white font-medium shadow-[0_4px_15px_rgba(229,9,20,0.3)] rounded-br-none'
                    : 'netflix-card text-gray-200 rounded-bl-none border border-white/10'
                }`}>
                  {msg.content}
                </div>
              </div>
            </div>
          ))}

          {/* Live Transcript Streaming Bubble */}
          {liveTranscript && (
            <div className="flex gap-3 justify-end animate-pulse">
              <div className="max-w-[88%] space-y-1 items-end text-right">
                <span className="text-[10px] font-bold text-[#E50914] uppercase tracking-wider block flex items-center justify-end gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E50914] animate-ping" />
                  Live Speaking...
                </span>
                <div className="p-3.5 rounded-2xl text-xs leading-relaxed bg-[#E50914]/20 border border-[#E50914]/40 text-white rounded-br-none shadow-lg">
                  {liveTranscript}
                </div>
              </div>
            </div>
          )}

          {/* Error Notices */}
          {voiceError && <p className="text-xs text-amber-400 bg-amber-950/30 border border-amber-500/20 p-3 rounded-xl">{voiceError}</p>}
          {responseError && <p className="text-xs text-red-400 bg-red-950/30 border border-red-500/20 p-3 rounded-xl">{responseError}</p>}

          <div ref={chatEndRef} />
        </div>

        {/* Integrated Controls Footer */}
        <div className="p-4 border-t border-white/10 bg-[#0D0D12] space-y-3 shrink-0">
          
          {/* Voice Mic Button */}
          <button
            data-testid="voice-input-btn"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={sending || ending || transcribing}
            className={`w-full py-3.5 px-4 rounded-2xl flex items-center justify-center gap-2 text-xs font-bold transition-all shadow-lg ${
              isRecording
                ? 'bg-red-600 text-white animate-pulse shadow-[0_0_20px_rgba(229,9,20,0.5)]'
                : transcribing
                ? 'bg-white/10 text-gray-400'
                : 'netflix-btn-red shadow-[0_0_20px_rgba(229,9,20,0.3)]'
            } disabled:opacity-40`}
          >
            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            <span>{isRecording ? 'Tap to Submit Answer' : transcribing ? 'Processing Speech...' : 'Tap Mic to Speak Answer'}</span>
          </button>

          {/* Text Input */}
          <div className="flex items-center gap-2 bg-[#050505] border border-white/15 rounded-2xl px-3 py-2.5 focus-within:border-[#E50914] transition-all">
            <input
              data-testid="interview-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Or type your message..."
              className="flex-1 bg-transparent text-xs text-white placeholder-gray-500 outline-none"
              disabled={sending || ending}
            />
            <button
              data-testid="send-message-btn"
              onClick={() => handleSend()}
              disabled={!input.trim() || sending || ending}
              className="p-2 rounded-xl bg-[#E50914] text-white hover:bg-[#FF1E27] transition-all disabled:opacity-20 shadow-md"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {showScreenPrompt && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="netflix-card rounded-3xl max-w-md w-full p-6 text-center border border-red-500/30 shadow-[0_0_50px_rgba(229,9,20,0.25)] relative">
            <button
              onClick={() => setShowScreenPrompt(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="w-14 h-14 rounded-2xl bg-[#E50914]/15 border border-[#E50914]/30 text-[#E50914] flex items-center justify-center mx-auto mb-4">
              <Monitor className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              Screen Recording Request
            </h3>
            <p className="text-gray-300 text-xs leading-relaxed mb-6">
              Start screen recording to capture your entire mock interview video. You can preview, download, and share your recorded interview on socials after the session!
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowScreenPrompt(false)}
                className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/15 font-semibold text-xs text-gray-300 transition"
              >
                Skip Recording
              </button>
              <button
                onClick={requestScreenRecording}
                className="netflix-btn-red flex-1 py-3 rounded-xl font-bold text-xs shadow-md flex items-center justify-center gap-2"
              >
                <Monitor className="w-4 h-4" /> Start Screen Record
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="netflix-card rounded-3xl max-w-xl w-full p-6 text-center border border-[#E50914]/40 shadow-[0_0_60px_rgba(229,9,20,0.3)] relative my-8">
            {/* Top Right Cross Button */}
            <button
              onClick={handleProceedToReport}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition"
              title="Close and Proceed to Report"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>
              Interview Complete! 🎉
            </h3>
            <p className="text-xs text-gray-300 mb-5">
              Your mock interview has been recorded. Review your video, download the session, or share your achievement on socials!
            </p>

            {/* Video Player Preview if Screen Recorded */}
            {screenRecordingUrl ? (
              <div className="mb-6 space-y-3">
                <div className="rounded-2xl overflow-hidden border border-white/15 bg-black shadow-inner">
                  <video src={screenRecordingUrl} controls className="w-full max-h-56 object-contain" />
                </div>
                <a
                  href={screenRecordingUrl}
                  download={`KevinAI_Interview_${interviewId}.webm`}
                  className="netflix-btn-red py-3 px-5 rounded-xl text-xs font-bold inline-flex items-center gap-2 shadow-lg"
                >
                  <Download className="w-4 h-4" /> Download Interview Video (.webm)
                </a>
              </div>
            ) : (
              <div className="p-4 mb-6 rounded-2xl bg-white/5 border border-white/10 text-xs text-gray-400">
                <p>No video screen recording was captured for this session.</p>
              </div>
            )}

            {/* Social Share Section */}
            <div className="netflix-card p-5 rounded-2xl border border-white/10 text-left mb-6 space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-200">
                <Share2 className="w-4 h-4 text-[#E50914]" />
                <span>Share your Kevin AI Interview on Socials:</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-semibold">
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent("I just completed a live AI Mock Interview with Kevin AI at https://www.kevinhr.in! Check it out to practice your tech interviews!")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="py-2.5 px-3 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 text-center transition truncate"
                >
                  WhatsApp
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://www.kevinhr.in")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="py-2.5 px-3 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 text-center transition truncate"
                >
                  LinkedIn
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://www.kevinhr.in")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="py-2.5 px-3 rounded-xl bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 border border-indigo-500/30 text-center transition truncate"
                >
                  Facebook
                </a>
                <button
                  onClick={handleCopyShareLink}
                  className="py-2.5 px-3 rounded-xl bg-pink-600/20 hover:bg-pink-600/30 text-pink-400 border border-pink-500/30 flex items-center justify-center gap-1.5 transition truncate"
                >
                  {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{linkCopied ? "Copied!" : "Instagram / Copy"}</span>
                </button>
              </div>
            </div>

            <button
              onClick={handleProceedToReport}
              className="netflix-btn-red w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(229,9,20,0.4)]"
            >
              <span>View Detailed Evaluation Report</span>
              <Sparkles className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes wave { from { height: 6px; } to { height: 32px; } }
        @keyframes ping-slow { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(1.35); opacity: 0; } }
        .animate-ping-slow { animation: ping-slow 2.2s ease-out infinite; }
      `}</style>
    </div>
  );
}
